import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters } from 'redux/types';
import { TShopifySalesChannel } from 'redux/reducers/shopifyStores/types';
import {
	deleteSalesChannelRequest,
	listSalesChannelsCatalogRequest,
	resetDeleteSalesChannelSuccess,
} from 'redux/reducers/shopifyStores/reducer';
import {
	selectDeleteSalesChannelLoading,
	selectDeleteSalesChannelSuccess,
	selectSalesChannelsCatalog,
	selectSalesChannelsCatalogLoading,
} from 'redux/reducers/shopifyStores/selectors';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { mapCatalogFiltersForApi } from './utils';
import { SalesChannelDialog } from './SalesChannelDialog';
import { DELETE_SALES_CHANNEL_ACTION, EDIT_SALES_CHANNEL_ACTION } from './constants';

export const ShopifySalesChannels: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [salesChannelToEdit, setSalesChannelToEdit] = useState<TShopifySalesChannel | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [salesChannelIdToDelete, setSalesChannelIdToDelete] = useState<number | null>(null);

	const catalogData = useSelector(selectSalesChannelsCatalog);
	const catalogLoading = useSelector(selectSalesChannelsCatalogLoading);
	const deleteLoading = useSelector(selectDeleteSalesChannelLoading);
	const deleteSuccess = useSelector(selectDeleteSalesChannelSuccess);

	if (deleteSuccess && (deleteDialogOpen || salesChannelIdToDelete !== null)) {
		setDeleteDialogOpen(false);
		setSalesChannelIdToDelete(null);
	}

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TShopifySalesChannel>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadSalesChannels = useCallback(() => {
		const params = queryString.parse(search);
		const filters = mapCatalogFiltersForApi(
			getFiltersFromNavigateParams(currentTableStructure, params) as TFilters,
		);

		dispatch(
			listSalesChannelsCatalogRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadSalesChannels();
	}, [loadSalesChannels]);

	useEffect(() => {
		if (!deleteSuccess) return;
		dispatch(resetDeleteSalesChannelSuccess());
		loadSalesChannels();
	}, [deleteSuccess, dispatch, loadSalesChannels]);

	const handleAddNew = useCallback(() => {
		setSalesChannelToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_SALES_CHANNEL_ACTION) {
				const row = catalogData?.data?.find((channel) => channel.id === id) ?? null;
				setSalesChannelToEdit(row);
				setDialogOpen(true);
				return;
			}

			if (action === DELETE_SALES_CHANNEL_ACTION) {
				setSalesChannelIdToDelete(id);
				setDeleteDialogOpen(true);
			}
		},
		[catalogData],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setSalesChannelToEdit(null);
		loadSalesChannels();
	}, [loadSalesChannels]);

	const handleDeleteDialogClose = useCallback(() => {
		if (deleteLoading) return;
		setDeleteDialogOpen(false);
		setSalesChannelIdToDelete(null);
	}, [deleteLoading]);

	const handleConfirmDelete = useCallback(() => {
		if (salesChannelIdToDelete == null) return;
		dispatch(deleteSalesChannelRequest({ channel_id: salesChannelIdToDelete }));
	}, [dispatch, salesChannelIdToDelete]);

	const salesChannelToDelete = catalogData?.data?.find(
		(channel) => channel.id === salesChannelIdToDelete,
	);

	return (
		<>
			<SalesChannelDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				salesChannelToEdit={salesChannelToEdit}
			/>
			<ConfirmationDialog
				open={deleteDialogOpen}
				setClose={handleDeleteDialogClose}
				handleConfirm={handleConfirmDelete}
				title="Delete Sales Channel"
				text={`Are you sure you want to delete sales channel "${salesChannelToDelete?.title ?? ''}"?`}
				disableConfirm={deleteLoading}
			/>
			<EasTableWithFilter<TShopifySalesChannel>
				tableStructure={currentTableStructure}
				items={catalogData}
				itemsIsLoading={catalogLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / <b>Shopify Sales Channels</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add Sales Channel
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
